import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  FileText, Download, ExternalLink, FolderOpen, ShieldCheck,
  Lock, Calendar,
} from 'lucide-react';
import BookSeniorModal from './BookSeniorModal';

// ★ 2026-08-13 (Fase 48) — Documenti progetto con accesso condizionato:
//   • Brochure → scaricabile pubblicamente (marketing)
//   • Permesso di costruzione / Foto & Render → RISERVATO
//       Per accedere l'utente deve prenotare una call con il Senior del paese.
// ★ 2026-08-14 (Fase 51) — Il "Fissa la call" ora apre il BookSeniorModal
//   unificato che mostra tutti i Senior disponibili del paese + il CEO come
//   opzione universale. Config centralizzata in lib/countrySeniors.js.

export default function ProjectDocuments({ documents, slug }) {
  const { t } = useTranslation();
  const [bookOpen, setBookOpen] = useState(false);
  const [bookContext, setBookContext] = useState({});

  const openBooking = (docTitle) => {
    setBookContext({
      subject: docTitle
        ? t('resortDetail.docs.dialogSubject.restrictedWithTitle', 'Documenti riservati · {{title}}', { title: docTitle })
        : t('resortDetail.docs.dialogSubject.restrictedGeneric', 'Documenti riservati'),
      sourceLabel: 'project_documents',
    });
    setBookOpen(true);
  };

  if (!documents) return null;
  const hasBrochure = !!documents.brochure;
  const hasPermesso = !!documents.permessoCostruzione;
  const hasFoto = !!documents.fotoProgetto;

  return (
    <div className="space-y-4" data-testid="project-documents">
      <div>
        <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.docs.eyebrow2', 'Documentazione')}</div>
        <h3 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] mt-1">{t('resortDetail.docs.title2', 'Documenti del progetto')}</h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-1.5 max-w-2xl">
          <Trans
            i18nKey="resortDetail.docs.description2"
            components={{ b: <b /> }}
          />
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Brochure — pubblicamente scaricabile */}
        {hasBrochure && (
          <Card
            className="hover:border-[hsl(var(--primary))] transition-colors bg-[var(--surface-1)]"
            data-testid="doc-card-brochure"
          >
            <CardContent className="p-4 flex flex-col h-full">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/12 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif font-semibold text-[hsl(var(--primary))]">{t('resortDetail.docs.brochureTitle', 'Brochure ufficiale')}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                    {t('resortDetail.docs.brochureDescription', 'Overview completo del progetto: masterplan, tipologie, prezzi.')}
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-2">
                <a
                  href={documents.brochure}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contents"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-9 text-xs"
                    data-testid="doc-download-brochure"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    {t('resortDetail.docs.brochureCta', 'Scarica brochure')}
                    <ExternalLink className="w-3 h-3 ml-1.5 opacity-60" />
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Permesso di costruzione — RISERVATO (call con senior) */}
        {hasPermesso && (
          <RestrictedDocCard
            testId="doc-card-permesso"
            title={t('resortDetail.docs.permessoTitle', 'Permesso di costruzione')}
            description={t('resortDetail.docs.permessoDescription', 'Documento ufficiale rilasciato dalle autorità locali.')}
            icon={ShieldCheck}
            onBook={openBooking}
          />
        )}

        {/* Foto & Render / Cartella progetto — RISERVATO */}
        {hasFoto && (
          <RestrictedDocCard
            testId="doc-card-foto"
            title={t('resortDetail.docs.fotoTitle', 'Foto & Render di cantiere')}
            description={t('resortDetail.docs.fotoDescription', 'Cartella Drive con render, planimetrie e foto del cantiere aggiornate.')}
            icon={FolderOpen}
            onBook={openBooking}
          />
        )}
      </div>

      {/* Note riservatezza */}
      <div className="text-[11px] md:text-xs text-muted-foreground italic bg-[var(--surface-2)]/50 border-l-2 border-[var(--gp-gold)] pl-3 py-2 rounded-r-md">
        {t('resortDetail.docs.confidentialityNote', 'I documenti riservati (permessi, foto di cantiere, planimetrie e dati fiscali) vengono condivisi solo durante la call one-to-one con il senior HCF del paese di riferimento — una precauzione per proteggere i nostri investitori e i partner locali.')}
      </div>

      {/* ★ 2026-08-14 (Fase 51) — Modal prenota Zoom con Senior/CEO */}
      <BookSeniorModal
        open={bookOpen}
        onOpenChange={setBookOpen}
        slug={slug}
        context={bookContext}
      />
    </div>
  );
}

// ── Card documento riservato ─────────────────────────────────────────────
function RestrictedDocCard({ testId, title, description, icon: Icon, onBook }) {
  const { t } = useTranslation();
  return (
    <Card
      className="relative overflow-hidden border-[var(--gp-gold)]/25 bg-gradient-to-br from-[var(--gp-gold)]/8 via-[var(--surface-1)] to-[var(--surface-1)]"
      data-testid={testId}
    >
      <div className="absolute top-2 right-2">
        <div className="inline-flex items-center gap-1 bg-[var(--gp-gold)]/20 text-[var(--gp-gold)] px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest">
          <Lock className="w-3 h-3" />
          {t('resortDetail.docs.restrictedBadge', 'Riservato')}
        </div>
      </div>
      <CardContent className="p-4 flex flex-col h-full">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--gp-gold)]/15 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[var(--gp-gold)]" />
          </div>
          <div className="flex-1 min-w-0 pr-16">
            <div className="font-serif font-semibold text-[hsl(var(--primary))]">{title}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{description}</div>
          </div>
        </div>

        <div className="mt-2 rounded-md bg-[var(--surface-1)] border border-[var(--gp-gold)]/20 p-2.5 text-[11px] space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            {t('resortDetail.docs.restrictedAccessTitle', 'Accesso via call')}
          </div>
          <div className="text-muted-foreground leading-snug">
            {t('resortDetail.docs.restrictedAccessDesc', 'Fissa una Zoom con il Senior HCF del paese — riceverai i documenti riservati durante la chiamata.')}
          </div>
        </div>

        <div className="mt-auto pt-3">
          <Button
            size="sm"
            className="w-full h-9 text-xs bg-[var(--gp-gold)] hover:bg-[var(--gp-gold)]/90 text-white"
            onClick={() => onBook?.(title)}
            data-testid={`${testId}-book-call`}
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            {t('resortDetail.docs.bookCta', 'Fissa la call')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
